import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Engagements, GetQueryRecordsQuery, GetQueryRecordsQueryVariables, QueryRecord } from '../../../../API';
import { AppService } from '../../../shared/services/app.service';
import { ApiService } from '../../../shared/services/api.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { GetQueryRecords } from '../../../../graphql/queries';
import { ChartDataService } from '../../services/chart-data.service';

@Component({
  selector: 'app-chart-data-preparer',
  templateUrl: './chart-data-preparer.component.html',
  styleUrl: './chart-data-preparer.component.scss',
})
export class ChartDataPreparerComponent implements OnInit {
  engagement!: string;
  isLoading = false;
  loadingQueries = false;

  selectedFilter = 'all';
  selectedSort = 'lastModified';
  selectedQuery: QueryRecord | null = null;

  sortDescending = true;
  selectedFilters: string[] = [];
  filterOptions: { label: string; value: string }[] = [];

  currentPage = 1;
  pageSize = 10;
  totalQueries = 0;
  nextBtnTooltip: string = '';

  allQueryResults: QueryRecord[] = [];
  queryResults: QueryRecord[] = [];
  @Output() goToNextStep = new EventEmitter<void>();

  constructor(
    private appService: AppService,
    private apiService: ApiService,
    private nzMessageService: NzMessageService,
    private chartDataService: ChartDataService,
  ) {}

  ngOnInit(): void {
    this.appService.activeEngagement$.subscribe((engagement: Engagements) => {
      this.engagement = engagement.engagement;
      this.getQueryResults();
    });
  }

  getQueryResults(): void {
    this.loadingQueries = true;
    this.apiService
      .query<GetQueryRecordsQuery, GetQueryRecordsQueryVariables>(GetQueryRecords, {
        engagementId: this.engagement,
        filterAggregateWithoutQueryProjection: true,
      })
      .then((response) => {
        this.allQueryResults = response.data?.GetQueryRecords as QueryRecord[];
        this.setFilterOptions();
        this.updatePaginatedResults();
        this.autoSelectFirstQuery();
      })
      .catch((error) => {
        this.nzMessageService.error(`${error} Failed to get query results`);
      })
      .finally(() => {
        this.loadingQueries = false;
      });
  }

  setFilterOptions(): void {
    this.filterOptions = this.allQueryResults
      .map((record: QueryRecord) => record.createdBy)
      .filter((createdBy, index, arr) => createdBy && arr.indexOf(createdBy) === index)
      .map((createdBy) => ({
        label: createdBy || 'Unknown',
        value: createdBy || 'Unknown',
      }));
  }

  updatePaginatedResults(): void {
    const filteredResults = this.getFilteredAndSortedResults();
    this.totalQueries = filteredResults.length;
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.queryResults = filteredResults.slice(startIndex, endIndex);
  }

  autoSelectFirstQuery(): void {
    if (this.queryResults.length > 0) {
      this.selectQuery(this.queryResults[0]);
    } else if (this.allQueryResults.length > 0) {
      this.selectQuery(this.allQueryResults[0]);
    } else {
      this.selectedQuery = null;
    }
  }

  getFilteredAndSortedResults(): QueryRecord[] {
    let results = [...this.allQueryResults];

    if (this.selectedFilters.length > 0) {
      results = results.filter((query) => this.selectedFilters.includes(query.createdBy || 'Unknown'));
    }

    if (this.sortDescending) {
      results.sort((a, b) => new Date(b.lastModifiedAt || 0).getTime() - new Date(a.lastModifiedAt || 0).getTime());
    } else {
      results.sort((a, b) => new Date(a.lastModifiedAt || 0).getTime() - new Date(b.lastModifiedAt || 0).getTime());
    }

    return results;
  }

  selectQuery(query: QueryRecord): void {
    this.selectedQuery = query;
    const metadata = this.selectedQuery.metadata || '{}';
    this.chartDataService.setType(metadata);
    this.chartDataService.queryRecord = this.selectedQuery;
  }

  onGoToNextStep() {
    this.goToNextStep.emit();
  }
  onPageChange(page: number): void {
    this.currentPage = page;
    this.updatePaginatedResults();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.updatePaginatedResults();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.updatePaginatedResults();
    this.autoSelectFirstQuery();
  }

  onSortChange(): void {
    this.currentPage = 1;
    this.updatePaginatedResults();
    this.autoSelectFirstQuery();
  }

  toggleSortDirection(): void {
    this.sortDescending = !this.sortDescending;
    this.currentPage = 1;
    this.updatePaginatedResults();
    this.autoSelectFirstQuery();
  }

  formatCellValue(value: unknown): string {
    if (typeof value === 'number') {
      if (value > 1000) {
        return value.toLocaleString();
      }
    }
    return value?.toString() || '';
  }

  getRowCount(query: QueryRecord): string {
    if (query.metadata) {
      try {
        const metadata = JSON.parse(query.metadata);
        return metadata.totalRecords ? metadata.totalRecords : '--';
      } catch {
        return '--';
      }
    }
    return '--';
  }
  get isQueryValid(): boolean {
    const rowCount = (this.selectedQuery && this.getRowCount(this.selectedQuery)) || 0;
    let isTypeAvailable = false;
    try {
      isTypeAvailable = this.selectedQuery?.metadata
        ? JSON.parse(this.selectedQuery?.metadata || '{}')?.columnsList?.every((col: any) => col.type)
        : false;
    } catch (e) {
      console.error('Error parsing metadata:', e);
    }
    if (!isTypeAvailable) {
      this.nextBtnTooltip = 'Data type for one or more column is not available from query result';
    } else if (rowCount == '0') {
      this.nextBtnTooltip = 'No Data available';
    } else if (rowCount == '--') {
      this.nextBtnTooltip = 'Invalid row count';
    } else {
      this.nextBtnTooltip = '';
    }
    return rowCount !== '--' && rowCount !== '0' && isTypeAvailable;
  }
}
